import React, { useState, useEffect, useRef } from 'react';
import api from './axiosConfig';
import './ChatDrawer.css';

const VIEWPORT_PAD = 8;
/** Max size used for drag/clamp math (matches .chat-toggle-btn max-width cap). */
const TOGGLE_MAX_WIDTH_PX = 196;
const TOGGLE_MIN_WIDTH_PX = 72;
/** Room for two label lines + tail (::after); used for clamp/position so short iframes don’t clip the pill. */
const TOGGLE_APPROX_HEIGHT_PX = 66;
/** Shown as the first assistant message when opening from the bubble with no draft and no messages (no API call). */
const OPENING_ASSISTANT_MESSAGE =
  "Rejara is designed to help families manage caregiving and aging with clarity and confidence. " +
  "If you're looking for a way to organize care, explore scenarios, and coordinate tasks, " +
  "Rejara can be a valuable resource for your needs. Please let us know your goals to check if Rejara is right for you.";

function toggleEffectiveWidth(viewportW) {
  const w = viewportW ?? (typeof window !== 'undefined' ? window.innerWidth : 400);
  return Math.min(TOGGLE_MAX_WIDTH_PX, Math.max(TOGGLE_MIN_WIDTH_PX, w - VIEWPORT_PAD * 2));
}

function toggleEffectiveHeight(viewportH) {
  const h = viewportH ?? (typeof window !== 'undefined' ? window.innerHeight : 600);
  return Math.min(TOGGLE_APPROX_HEIGHT_PX, Math.max(40, h - VIEWPORT_PAD * 2));
}

function clampTogglePosition(x, y) {
  const w = typeof window !== 'undefined' ? window.innerWidth : 400;
  const h = typeof window !== 'undefined' ? window.innerHeight : 600;
  const btnW = toggleEffectiveWidth(w);
  const btnH = toggleEffectiveHeight(h);
  const maxX = Math.max(VIEWPORT_PAD, w - btnW - VIEWPORT_PAD);
  const maxY = Math.max(VIEWPORT_PAD, h - btnH - VIEWPORT_PAD);
  return {
    x: Math.max(VIEWPORT_PAD, Math.min(x, maxX)),
    y: Math.max(VIEWPORT_PAD, Math.min(y, maxY)),
  };
}

/** Stable id for this browser profile; backend uses it as session_id in logs (see ChatMessageRequest.user_guid). */
const CHAT_CLIENT_SESSION_KEY = 'rejara_chat_client_session_id';

function getOrCreateChatClientSessionId() {
  try {
    let id = window.localStorage.getItem(CHAT_CLIENT_SESSION_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      window.localStorage.setItem(CHAT_CLIENT_SESSION_KEY, id);
    }
    return id;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }
}

function ChatDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [position, setPosition] = useState(() =>
    clampTogglePosition(
      window.innerWidth - toggleEffectiveWidth() - VIEWPORT_PAD,
      window.innerHeight - toggleEffectiveHeight() - VIEWPORT_PAD
    )
  );
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [typingDelay] = useState(20); // Typing speed in milliseconds
  const [userTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [chatClientSessionId] = useState(() => getOrCreateChatClientSessionId());
  const messagesEndRef = useRef(null);
  const buttonRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const chatInputRef = useRef(null);
  const messagesRef = useRef([]);
  messagesRef.current = messages;
  const isEmbedded = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();

  const positionStorageKey = isEmbedded
    ? 'chatButtonPositionEmbedded'
    : 'chatButtonPosition';

  // On load: if URL has ?q=..., prefill the composer only (do not open chat or auto-send)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (!q || !q.trim()) return;
    try {
      setUserInput(decodeURIComponent(q.trim()));
    } catch {
      setUserInput(q.trim());
    }
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Notify parent page when widget opens/closes (for iframe resize in bookmarklet embed mode)
  useEffect(() => {
    if (!isEmbedded) return;
    try {
      window.parent.postMessage(
        { type: 'rejara-widget-toggle', open: isOpen },
        '*'
      );
    } catch {
      // no-op
    }
  }, [isOpen, isEmbedded]);

  // Focus input when drawer opens
  useEffect(() => {
    if (isOpen && chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [isOpen]);

  // Focus input after bot finishes typing
  useEffect(() => {
    const hasTypingMessage = messages.some(msg => msg.isTyping);
    if (!loading && !hasTypingMessage && chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [loading, messages]);

  // Helper function to format timestamp with date and time for display
  const formatTimestamp = (date = null) => {
    // If no date provided or null/undefined, use current time
    if (!date) {
      date = new Date();
    }
    // If date is a string, parse it as local time in user's timezone
    else if (typeof date === 'string') {
      // If it's an ISO timestamp without timezone, treat it as UTC
      if (date.includes('T') && !date.includes('Z') && !date.includes('+') && !date.includes('-', 10)) {
        date = new Date(date + 'Z'); // Add Z to treat as UTC
      } else {
        date = new Date(date);
      }
    }
    
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: userTimezone
    });
  };

  // Don't load chat history automatically - start fresh each session
  // Chat history is saved to database but not displayed in new sessions
  // useEffect(() => {
  //   if (userGuid) {
  //     loadChatHistory();
  //   }
  // }, [userGuid]);

  // Load saved position (embed uses its own key so full-page coords don't break tiny iframe)
  useEffect(() => {
    const savedPosition = localStorage.getItem(positionStorageKey);
    if (savedPosition) {
      try {
        const p = JSON.parse(savedPosition);
        setPosition(clampTogglePosition(p.x, p.y));
      } catch {
        setPosition(
          clampTogglePosition(
            window.innerWidth - toggleEffectiveWidth() - VIEWPORT_PAD,
            window.innerHeight - toggleEffectiveHeight() - VIEWPORT_PAD
          )
        );
      }
    } else {
      setPosition(
        clampTogglePosition(
          window.innerWidth - toggleEffectiveWidth() - VIEWPORT_PAD,
          window.innerHeight - toggleEffectiveHeight() - VIEWPORT_PAD
        )
      );
    }
  }, [positionStorageKey]);

  // Save position to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(positionStorageKey, JSON.stringify(position));
  }, [position, positionStorageKey]);

  // Keep toggle inside viewport when iframe is resized (e.g. bookmarklet expands)
  useEffect(() => {
    const onResize = () => {
      setPosition((prev) => clampTogglePosition(prev.x, prev.y));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Handle mouse down for dragging
  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // Only left mouse button
    
    const rect = buttonRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setIsDragging(true);
    e.preventDefault();
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    // Keep button within viewport bounds with padding
    const maxX = window.innerWidth - toggleEffectiveWidth() - VIEWPORT_PAD;
    const maxY = window.innerHeight - toggleEffectiveHeight() - VIEWPORT_PAD;

    setPosition({
      x: Math.max(VIEWPORT_PAD, Math.min(newX, maxX)),
      y: Math.max(VIEWPORT_PAD, Math.min(newY, maxY)),
    });
  };

  // Handle mouse up to stop dragging
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none'; // Prevent text selection while dragging
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    };
  }, [isDragging, dragOffset]);

  // Typewriter effect function
  const typeText = (messageId, text, callback) => {
    let i = 0;
    
    const typeChar = () => {
      if (i < text.length) {
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, content: text.substring(0, i + 1), isTyping: true }
            : msg
        ));
        i++;
        
        // Scroll to bottom every 5 characters
        if (i % 5 === 0 && chatMessagesRef.current) {
          chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
        
        setTimeout(typeChar, typingDelay);
      } else {
        // Finish typing
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, content: text, isTyping: false }
            : msg
        ));
        if (chatMessagesRef.current) {
          chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
        if (callback) callback();
      }
    };
    
    typeChar();
  };

  // Add message with typing effect
  const addMessage = (type, content, timestamp) => {
    const newMessage = {
      id: Date.now(),
      type,
      content: '',
      timestamp,
      isTyping: type === 'assistant'
    };

    setMessages(prev => [...prev, newMessage]);

    if (type === 'assistant') {
      // Use typewriter effect for assistant messages
      typeText(newMessage.id, content, () => {
        setLoading(false);
      });
    } else {
      // For user messages, set content immediately
      setMessages(prev => prev.map(msg => 
        msg.id === newMessage.id ? { ...msg, content, isTyping: false } : msg
      ));
    }
  };

  const submitMessage = async (rawText) => {
    const trimmed = (rawText || '').trim();
    if (!trimmed || loading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: trimmed,
      timestamp: formatTimestamp()
    };

    setMessages((prev) => [...prev, userMessage]);
    setUserInput('');
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/chat', {
        user_guid: chatClientSessionId,
        message: trimmed,
        chat_history: messages.map((msg) => ({
          type: msg.type,
          content: msg.content
        }))
      });

      if (response.data.success) {
        addMessage(
          'assistant',
          response.data.response,
          formatTimestamp(response.data.timestamp)
        );
      } else {
        setError(response.data.message || 'Failed to send message');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    await submitMessage(userInput);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  return (
    <>
      {/* Floating bubble only when closed — when open, close via header (avoids toggle stuck top-left after iframe resize) */}
      {!isOpen && (
      <button 
        ref={buttonRef}
        type="button"
        className={`chat-toggle-btn${isDragging ? ' dragging' : ''}${isEmbedded ? ' chat-toggle-btn--embedded' : ''}`}
        onClick={(e) => {
          if (!isDragging) {
            const prefilled = userInput.trim();
            setIsOpen(true);
            // After open: optional ?q/draft sends to API; otherwise seed a static opening assistant message (no default user question).
            queueMicrotask(() => {
              if (loading) return;
              if (messagesRef.current.length > 0) return;
              const text = prefilled;
              if (text) {
                submitMessage(text);
              } else {
                setMessages([
                  {
                    id: Date.now(),
                    type: 'assistant',
                    content: OPENING_ASSISTANT_MESSAGE,
                    timestamp: formatTimestamp(),
                    isTyping: false,
                  },
                ]);
              }
            });
          }
        }}
        onMouseDown={handleMouseDown}
        title="Drag to move"
        aria-label="Is Rejara right for me? Open chat. Drag to move."
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          position: 'fixed'
        }}
      >
        <span className="chat-toggle-label">Is Rejara right for me?</span>
        {/*
        Previous floating bubble (Rejara logo)
        <img
          className="chat-toggle-logo"
          src="data:image/svg+xml,%3Csvg%20width%3D%22210%22%20height%3D%22210%22%20viewBox%3D%220%200%20210%20210%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Ccircle%20cx%3D%22105%22%20cy%3D%22105%22%20r%3D%2278%22%20fill%3D%22%233A6D8C%22%2F%3E%3Cg%20transform%3D%22translate(9%2C34)%20scale(1.75)%22%20fill%3D%22white%22%3E%3Cpath%20d%3D%22M32.2033%2036.1175C27.3364%2036.1175%2023.3898%2040.0641%2023.3898%2044.9341H48.0393C52.9093%2044.9341%2056.8559%2040.9875%2056.8559%2036.1175H32.2033Z%22%2F%3E%3Cpath%20d%3D%22M48.0393%2048.3774H37.972C37.972%2050.8108%2038.9571%2053.0157%2040.5537%2054.6123C42.1471%2056.2058%2044.3521%2057.194%2046.7855%2057.194H56.8559C56.8559%2052.324%2052.9093%2048.3774%2048.0393%2048.3774Z%22%2F%3E%3Cpath%20d%3D%22M56.8559%2032.6742H32.2033C27.3364%2032.6742%2023.3898%2028.7276%2023.3898%2023.8607H48.0393C52.9093%2023.8607%2056.8559%2027.8073%2056.8559%2032.6742Z%22%2F%3E%3C%2Fg%3E%3Crect%20x%3D%22123%22%20y%3D%2282%22%20width%3D%2238%22%20height%3D%2226%22%20rx%3D%228%22%20fill%3D%22white%22%2F%3E%3Cpolygon%20points%3D%22123%2C89%20107%2C100%20123%2C97%22%20fill%3D%22white%22%2F%3E%3Ccircle%20cx%3D%22133%22%20cy%3D%2295%22%20r%3D%222.5%22%20fill%3D%22%233A6D8C%22%2F%3E%3Ccircle%20cx%3D%22142%22%20cy%3D%2295%22%20r%3D%222.5%22%20fill%3D%22%233A6D8C%22%2F%3E%3Ccircle%20cx%3D%22151%22%20cy%3D%2295%22%20r%3D%222.5%22%20fill%3D%22%233A6D8C%22%2F%3E%3C%2Fsvg%3E"
          alt=""
        />
        */}
      </button>
      )}

      {/* Chat Drawer */}
      <div 
        className={`chat-drawer ${isOpen ? 'open' : ''}`}
        style={{
          left: isEmbedded ? '12px' : `${Math.min(Math.max(position.x - 320, 20), window.innerWidth - 420)}px`,
          top: isEmbedded ? '12px' : `${Math.max(position.y - 520, 20)}px`,
          right: isEmbedded ? '12px' : 'auto',
          bottom: isEmbedded ? '12px' : 'auto',
          width: isEmbedded ? 'min(420px, calc(100vw - 24px))' : undefined,
          height: isEmbedded ? 'min(640px, calc(100vh - 24px))' : undefined,
          position: 'fixed'
        }}
      >
        <div className="chat-header">
          <div className="chat-header-spacer" aria-hidden="true" />
          <div className="chat-title">
            <span>Chat with Rejara</span>
          </div>
          <button
            type="button"
            className="chat-header-close"
            onClick={() => setIsOpen(false)}
            aria-label="Close chat"
          >
            ×
          </button>
        </div>

        <div className="chat-messages" ref={chatMessagesRef}>
          {messages.length === 0 ? null : (
            messages.map((message) => (
              <div key={message.id} className={`message ${message.type}`}>
                <div className="message-content">
                  <p dangerouslySetInnerHTML={{ __html: message.content.replace(/\n/g, '<br />') }}></p>
                  {message.isTyping && <span className="typing-cursor">|</span>}
                  <span className="message-time">{message.timestamp}</span>
                </div>
              </div>
            ))
          )}
          
          {loading && (
            <div className="message assistant">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSendMessage} className="chat-input-form">
          <div className="chat-input-container">
            <textarea
              ref={chatInputRef}
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={loading}
              className="chat-input"
              rows="2"
            />
            <button 
              type="submit" 
              disabled={loading || !userInput.trim()}
              className="chat-send-btn"
            >
              <i className="fas fa-paper-plane"></i>
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default ChatDrawer;
